import { account, resolveAccountKindFromProfile } from "./appwrite.js";
import { fetchSubscriptionViaGateway } from "./stripe.js";
import { toUserFacingBillingError } from "./userFacingErrors.js";

export const PAYING_HEYCOACH_STATUSES = [
  "active",
  "trialing",
  "past_due",
  "paused",
];

export function isPayingHeycoachSubscriber(doc) {
  if (!doc) return false;
  const s = String(doc.status ?? "").trim().toLowerCase();
  return PAYING_HEYCOACH_STATUSES.includes(s);
}

/**
 * Appwrite Account `prefs.accountKind`:
 *   - `"client"` — end user who buys programs / pays their coach through the site.
 *   - omit or `"coach"` — HeyCoach coach (may not have a platform subscription yet).
 */
export function isClientAccountKind(user) {
  return String(user?.prefs?.accountKind ?? "").trim() === "client";
}

/**
 * Resolve coach vs client for routing. Prefers Auth prefs; falls back to
 * clients/coaches profile documents when accountKind was never set.
 */
export async function resolveRoleForUser(user) {
  if (isClientAccountKind(user)) return "client";

  const fromProfile = await resolveAccountKindFromProfile(user?.$id, user?.email);
  if (fromProfile === "client") return "client";

  const pref = String(user?.prefs?.accountKind ?? "").trim();
  if (pref === "coach") return "coach";
  if (fromProfile === "coach") return "coach";

  return "client";
}

/** Default landing after sign-in when no `location.state.from`. */
export function resolvePostLoginPathForRole(role) {
  return role === "client" ? "/programs" : "/dashboard";
}

const COACH_ONLY_PATHS = new Set([
  "/dashboard",
  "/billing",
  "/client-plans",
  "/settings",
  "/plan",
]);

function isClientSafePath(pathname) {
  if (!pathname || typeof pathname !== "string") return false;
  if (pathname === "/programs") return true;
  if (pathname.startsWith("/programs/")) return true;
  if (pathname === "/settings" || pathname === "/billing-settings" || pathname === "/success") {
    return true;
  }
  return false;
}

/** Honor `from` only when the signed-in role may access that route. */
export function resolveSafePostLoginPath(fromPathname, role) {
  const fallback = resolvePostLoginPathForRole(role);
  if (role === "client") {
    if (!isClientSafePath(fromPathname)) return fallback;
    return fromPathname;
  }
  if (fromPathname && COACH_ONLY_PATHS.has(fromPathname)) return fromPathname;
  if (fromPathname && typeof fromPathname === "string") return fromPathname;
  return fallback;
}

/** @deprecated Prefer resolvePostLoginPathForRole after resolveRoleForUser */
export function resolvePostLoginPath(user) {
  if (isClientAccountKind(user)) return "/programs";
  return "/dashboard";
}

export function normalizeHeycoachSubscriptionDoc(raw) {
  if (!raw) return null;
  return { ...raw, planId: String(raw.planId ?? "").trim() };
}

/**
 * Fetch subscription state directly from the gateway, which in turn calls Stripe API.
 * Returns { doc, loadError }.
 *
 * The gateway's subscription-summary endpoint reads live data from Stripe using the
 * user's stripeCustomerId stored in Appwrite prefs — no subscriptions collection required.
 */
export async function fetchHeycoachSubscriptionDocDetailedForUserId(userId) {
  let raw = null;
  let loadError = null;

  try {
    const data = await fetchSubscriptionViaGateway();

    const gate = data.subscription ?? null;
    if (gate && String(gate.userId ?? "") === String(userId)) {
      raw = gate;
    } else if (gate) {
      loadError =
        "We couldn't load your subscription for this account. Please sign out and back in, or contact support.";
    } else {
      const hint = data?.subscriptionHint;
      if (hint?.code === "NO_STRIPE_CUSTOMER") {
        loadError = null; // Not an error — user simply hasn't subscribed yet.
      } else if (hint?.code === "NO_SUBSCRIPTION_FOR_CUSTOMER") {
        loadError = null; // Same: valid "no plan" state.
      }
    }
  } catch (e) {
    loadError = toUserFacingBillingError(e?.message ?? String(e), {
      fallback: "We couldn't load your subscription right now. Please refresh the page.",
    });
  }

  const doc = normalizeHeycoachSubscriptionDoc(raw);

  return {
    doc,
    loadError: doc ? null : loadError,
  };
}

export async function fetchHeycoachSubscriptionDocForUserId(userId) {
  const { doc } = await fetchHeycoachSubscriptionDocDetailedForUserId(userId);
  return doc;
}

/** Prefer after a session exists (e.g. post-login). */
export async function fetchHeycoachSubscriptionDoc() {
  const user = await account.get();
  return fetchHeycoachSubscriptionDocForUserId(user.$id);
}

/**
 * Default landing after sign-in when no `location.state.from`.
 * Client account kind always wins — clients can have subscriptions too.
 */
export async function getPostLoginPath() {
  const user = await account.get();
  const role = await resolveRoleForUser(user);
  return resolvePostLoginPathForRole(role);
}
